import { jwtDecode } from "jwt-decode";
import { setSentryUser } from "../integrations/sentry/sentryInit";
import { resetAnalytics } from "./analytics";
import { stopGuides } from "./guides";
import {
  CLAIM_TYPES,
  // PLATFORM_AUTH_URL,
  // PLATFORM_LOGOUT_URL,
  CLAIMS,
  LEGACY_SCOPES,
  MK_AUTH_OAUTH_AUTHORIZE_URL,
  MK_AUTH_CLIENT_ID,
  PLATFORM_LOGOUT_URL,
} from "./consts";
import { isEmpty } from "./jsUtils";
import { POST_LOGIN_REDIRECT_SESSION_KEY } from "../const";
import type { RbacClaim } from "./rbac";

/**
 * Fallback UUID generator for environments where crypto.randomUUID() may not be
 * available due to secure context requirements (HTTPS) or older browser versions.
 */
function generateFallbackUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = crypto.getRandomValues(new Uint8Array(1))[0] | 0;
    const v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const {
  dnbDataEnabled,
  exportAbility,
  creditsEnforcementEnabled,
  rgifPlatformEnabled,
  hgForceEnabled,
} = CLAIM_TYPES;

export function loginMkAuth() {
  const state = crypto.randomUUID
    ? crypto.randomUUID()
    : generateFallbackUUID();
  const redirectUri = `${window.location.origin}/auth/callback`;

  // Preserve deep links across mk-auth redirects. /login (where clearSession
  // lands) is not a deep link — restoring it would leave the user on a blank page.
  try {
    if (
      !["/auth/callback", "/login"].includes(window.location.pathname) &&
      !sessionStorage.getItem(POST_LOGIN_REDIRECT_SESSION_KEY)
    ) {
      sessionStorage.setItem(
        POST_LOGIN_REDIRECT_SESSION_KEY,
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      );
    }
  } catch {
    // Best effort only
  }

  // Store state for verification after callback
  sessionStorage.setItem("oauth_state", state);

  const authUrl = new URL(MK_AUTH_OAUTH_AUTHORIZE_URL);
  authUrl.searchParams.set("client_id", MK_AUTH_CLIENT_ID);
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("scope", "openid profile email");
  authUrl.searchParams.set("state", state);

  window.location.href = authUrl.href;
}

export async function logout() {
  const accessToken = localStorage.getItem("access_token");

  if (accessToken) {
    try {
      // Call the server to handle OAuth logout
      await fetch(PLATFORM_LOGOUT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ access_token: accessToken }),
      });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  }

  clearSession();
}

export function clearSessionNoRedirect() {
  localStorage.removeItem("impersonation_state");
  localStorage.removeItem("original_tokens");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("id_token");
  localStorage.removeItem("expires_at");
  localStorage.removeItem("profile");
  localStorage.removeItem("claims");
  localStorage.removeItem("oktaGroups");

  setSentryUser(null);
  resetAnalytics();
  stopGuides();
}

export function clearSession() {
  clearSessionNoRedirect();

  window.location.pathname = "/login";
}

export function isAuthenticated(): boolean {
  const expiresAtRaw = localStorage.getItem("expires_at");
  const expiresAt =
    expiresAtRaw === "undefined" ? undefined : JSON.parse(expiresAtRaw || "0");

  return Date.now() < expiresAt;
}
export interface UserProfile {
  email: string;
  oktaUserId: string;
}

export function getLoggedUserProfile(): UserProfile | null {
  const profile = localStorage.getItem("profile");
  return profile ? JSON.parse(profile) : null;
}

export function getUserRole(): string | null {
  const raw = localStorage.getItem(CLAIMS);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed.platformPerms?.role ?? parsed.role ?? null;
  } catch {
    return null;
  }
}

export function isReadOnlyUser(): boolean {
  const accessToken = localStorage.getItem("access_token");
  if (!accessToken) return false;
  try {
    const { mkClaims } = jwtDecode<{
      mkClaims?: { rbac?: { role?: string } };
    }>(accessToken);
    return mkClaims?.rbac?.role === "readOnly";
  } catch {
    return false;
  }
}

/**
 * Reads the RBAC v2 claim and the `superKudu` flag from the access-token JWT
 * (`mkClaims`) — the ONLY place they exist. `getUserClaims()` reads the stored
 * `claims` object (hgClaims: groups/userId/platformPerms), which has neither, so
 * `getUserClaims().rbac`/`.superKudu` are always `undefined` and RBAC v2 checks
 * built on them silently fall back to the v1 (full-access) path. RBAC v2 gates
 * should source `rbac`/`superKudu` from here instead; this wires the
 * Save-as-Workbook gate, and other callsites can migrate the same way.
 *
 * Returns `{ rbac: undefined, superKudu: false }` for v1 tokens or parse
 * failures, preserving the intended v1 short-circuit in the predicates.
 */
export function getRbacClaims(): { rbac?: RbacClaim; superKudu: boolean } {
  const accessToken = localStorage.getItem("access_token");
  if (!accessToken) return { rbac: undefined, superKudu: false };
  try {
    const { mkClaims } = jwtDecode<{
      mkClaims?: { rbac?: RbacClaim; superKudu?: boolean };
    }>(accessToken);
    return { rbac: mkClaims?.rbac, superKudu: !!mkClaims?.superKudu };
  } catch {
    return { rbac: undefined, superKudu: false };
  }
}

/**
 * Whether the current user may create/edit/delete scoring profiles. Managing
 * scoring configuration lives in Admin, so it is admin-only:
 *
 * - SuperKudu: always.
 * - RBAC v2 tenants (`mkClaims.rbac`): only `orgAdmin`; `standard` is view-only.
 * - V1 tenants (`mkClaims.permissions`): `admin` or `architect`; plain `user`
 *   is view-only, and `salesUser` never reaches Market Analyzer (the app shell
 *   is gated behind the `market_analyzer` module).
 *
 * Reads the roles straight from the access-token JWT — the V1 role lives in
 * `mkClaims.permissions[].role` (not `platformPerms`, so `getUserRole()` can't
 * see it) and the V2 role in `mkClaims.rbac.role`. Returns `false` for a
 * missing/unparseable token, the safe default for a write gate.
 */
export function canManageScoring(): boolean {
  const accessToken = localStorage.getItem("access_token");
  if (!accessToken) return false;
  try {
    const { mkClaims } = jwtDecode<{
      mkClaims?: {
        superKudu?: boolean;
        rbac?: { role?: string };
        permissions?: { role?: string }[];
      };
    }>(accessToken);

    if (mkClaims?.superKudu) return true;

    // RBAC v2 tenants: everyone but readOnly manages scoring. Same rule the admin
    // app applies to the same objects (useUserRole.canEditScoring), and bongo's
    // scoring routes carry no role gate at all — so requiring orgAdmin here made
    // Market Analyzer stricter than both the API and the other UI, and a Data
    // Studio standard user who can create a model in admin found the button here
    // disabled, under a tooltip saying only admins may.
    if (mkClaims?.rbac) return !isReadOnlyUser();

    // V1 tenants: admin + architect manage; mk-auth reads the first permission.
    const v1Role = mkClaims?.permissions?.[0]?.role;
    return v1Role === "admin" || v1Role === "architect";
  } catch {
    return false;
  }
}

/**
 * Reads the numeric mk-auth tenant id (as a string) from the access-token JWT,
 * or null if absent. Same `jwtDecode` pattern as `getRbacFromAccessToken` and
 * `navigation-config.tsx`'s `getOrgId`. Used as the `tenant_id` argument for the
 * V2 (argo model) scoring path in `opportunity_generator_search`.
 */
export function getTenantId(): string | null {
  try {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return null;
    // `mkClaims.tenantId` is numeric in the JWT, so coerce to a string — the
    // cs-api `opportunity_generator_search` `tenant_id` arg is typed `String`
    // (hermes rejects a non-string tenant), and the admin URL builder wants a
    // string too.
    const decoded = jwtDecode<{ mkClaims?: { tenantId?: string | number } }>(
      accessToken
    );
    const tenantId = decoded.mkClaims?.tenantId;
    return tenantId == null || tenantId === "" ? null : String(tenantId);
  } catch (error) {
    console.error("Error decoding JWT for tenant id:", error);
    return null;
  }
}

/**
 * The Overseer org id mk-auth stamps on the access token (`hgClaims.orgId`,
 * the tenant's `global_id`), or null. company-match-api files an upload under
 * this same claim, so it is what covered-wagon's rematch must mint its
 * company-match-api token for (the Universe JSON's `companyMatch.orgId`).
 * Falls back to the stored claims, which are the same `hgClaims` object.
 */
export function getHgOrgId(): string | null {
  try {
    const accessToken = localStorage.getItem("access_token");
    if (accessToken) {
      const decoded = jwtDecode<{ hgClaims?: { orgId?: string | null } }>(
        accessToken
      );
      if (decoded.hgClaims?.orgId) return decoded.hgClaims.orgId;
    }
    const storedClaims = localStorage.getItem(CLAIMS);
    if (!storedClaims) return null;
    const parsed = JSON.parse(storedClaims) as { orgId?: string | null };
    return parsed.orgId || null;
  } catch {
    return null;
  }
}

export function getUserClaims() {
  const userClaims = localStorage.getItem(CLAIMS);
  if (!userClaims) return {};

  const parsed = JSON.parse(userClaims);

  // Handle both old format (flat) and new format (with platformPerms)
  const claims = parsed.platformPerms
    ? { ...parsed.platformPerms, superKudu: parsed.superKudu }
    : parsed;

  // The stored object is hgClaims and has never carried `rbac`, so every
  // RBAC v2 gate reading `getUserClaims().rbac` saw a v1 user: org admins got
  // the v1 sidebar and no app switcher (RGI-2609). Source `rbac` (and
  // `superKudu` when the object lacks it) from the access token, like Admin
  // and Sales Copilot do.
  const fromToken = getRbacClaims();
  return {
    ...claims,
    rbac: claims.rbac ?? fromToken.rbac,
    superKudu: claims.superKudu ?? fromToken.superKudu,
  };
}

export function userHasClaims(claims: string[]): boolean {
  const grantedClaims = getUserClaims();

  return (
    !isEmpty(grantedClaims) &&
    claims.every((claim) => Boolean(grantedClaims[claim]))
  );
}

// MAY IS NOT NEEDED THIS PART, JUST COPIED FROM LEGACY CODE
export function userHasOktaGroups(oktaGroupsRequired: string[]): boolean {
  const oktaGroupsAssigned = JSON.parse(
    localStorage.getItem("oktaGroups") || "[]"
  );
  return (
    !isEmpty(oktaGroupsAssigned) &&
    oktaGroupsRequired.every((oktaGroupRequired) =>
      oktaGroupsAssigned.includes(oktaGroupRequired)
    )
  );
}

export function getUserScopes(): string {
  const scopes = [];
  const claims = getUserClaims();

  // MAY IS NOT NEEDED THIS PART, JUST COPIED FROM LEGACY CODE
  for (const scope of Object.keys(LEGACY_SCOPES)) {
    if (claims && claims[scope] === true) {
      scopes.push(LEGACY_SCOPES[scope as keyof typeof LEGACY_SCOPES]);
    }
  }

  if (userHasOktaGroups(["Intent"])) {
    scopes.push("access:intentOG");
  }

  return scopes.join(" ");
}
